import React from 'react';
import { SectionInstance } from '../types';

// Navigation
import NavStandard from './NavStandard';
import NavMega from './NavMega';
// Heroes
import HeroCentered from './HeroCentered';
import HeroSplit from './HeroSplit';
import HeroVideo from './HeroVideo';
import HeroMinimal from './HeroMinimal';
// Trust
import TrustBar from './TrustBar';
import StatsBar from './StatsBar';
import LogoCloud from './LogoCloud';
import AwardsBadges from './AwardsBadges';
import BeforeAfter from './BeforeAfter';
import SocialProofBar from './SocialProofBar';
// Features
import FeaturesGrid from './FeaturesGrid';
import FeaturesAlternating from './FeaturesAlternating';
import FeaturesTabs from './FeaturesTabs';
// Services
import ServicesCards from './ServicesCards';
import ServicesDetailed from './ServicesDetailed';
import PricingTable from './PricingTable';
// Content
import ContentTextImage from './ContentTextImage';
import ContentRichText from './ContentRichText';
import ContentTimeline from './ContentTimeline';
// Media
import TeamGrid from './TeamGrid';
import ContactMap from './ContactMap';
import GalleryGrid from './GalleryGrid';
import VideoSection from './VideoSection';
// Testimonials
import TestimonialsCarousel from './TestimonialsCarousel';
import TestimonialsGrid from './TestimonialsGrid';
// FAQ
import FaqAccordion from './FaqAccordion';
import FaqTwoColumn from './FaqTwoColumn';
// CTA
import CtaBanner from './CtaBanner';
import LeadFormInline from './LeadFormInline';
import QuizEmbed from './QuizEmbed';
// Footers
import FooterStandard from './FooterStandard';
import FooterMinimal from './FooterMinimal';
// Compliance
import ComplianceBanner from './ComplianceBanner';
import CookieConsentBanner from './CookieConsentBanner';
// Integrations
import BlogFeed from './BlogFeed';
// Batch 1 additions
import HeroGradient from './HeroGradient';
import HeroDiagonal from './HeroDiagonal';
import HeroCards from './HeroCards';
import ProcessSteps from './ProcessSteps';
import PortfolioGrid from './PortfolioGrid';
import CaseStudyCards from './CaseStudyCards';
import ContactSplit from './ContactSplit';
import LogoMarquee from './LogoMarquee';
import IconBoxes from './IconBoxes';
import MetricsLarge from './MetricsLarge';
// Batch 2 additions
import ParallaxSection from './ParallaxSection';
import AnnouncementBar from './AnnouncementBar';
import AccordionServices from './AccordionServices';
import FooterMultiColumn from './FooterMultiColumn';
import CtaSplit from './CtaSplit';
import ComparisonTable from './ComparisonTable';
import ImageCards from './ImageCards';
import DividerWave from './DividerWave';
import NumberCounter from './NumberCounter';
import TestimonialFeatured from './TestimonialFeatured';
// Pricing calculator (T14)
import PricingCalculator from './PricingCalculator';
// RevFX visual effects (POC)
import Effect from './Effect';

// Renderer dispatch table — maps component_id to React component
const RENDERERS: Record<string, React.FC<{ section: SectionInstance }>> = {
  effect: Effect,
  // Navigation
  nav_standard: NavStandard,
  nav_mega: NavMega,
  // Heroes
  hero_centered: HeroCentered,
  hero_split: HeroSplit,
  hero_video: HeroVideo,
  hero_minimal: HeroMinimal,
  // Trust
  trust_bar: TrustBar,
  stats_bar: StatsBar,
  logo_cloud: LogoCloud,
  awards_badges: AwardsBadges,
  before_after: BeforeAfter,
  social_proof_bar: SocialProofBar,
  // Features
  features_grid: FeaturesGrid,
  features_alternating: FeaturesAlternating,
  features_tabs: FeaturesTabs,
  // Services
  services_cards: ServicesCards,
  services_detailed: ServicesDetailed,
  pricing_table: PricingTable,
  // Content
  content_text_image: ContentTextImage,
  content_rich_text: ContentRichText,
  content_timeline: ContentTimeline,
  // Media
  team_grid: TeamGrid,
  contact_map: ContactMap,
  gallery_grid: GalleryGrid,
  video_section: VideoSection,
  // Testimonials
  testimonials_carousel: TestimonialsCarousel,
  testimonials_grid: TestimonialsGrid,
  // FAQ
  faq_accordion: FaqAccordion,
  faq_two_column: FaqTwoColumn,
  // CTA
  cta_banner: CtaBanner,
  lead_form_inline: LeadFormInline,
  quiz_embed: QuizEmbed,
  // Footers
  footer_standard: FooterStandard,
  footer_minimal: FooterMinimal,
  // Compliance
  compliance_banner: ComplianceBanner,
  cookie_consent_banner: CookieConsentBanner,
  // Integrations
  blog_feed: BlogFeed,
  // Batch 1 additions
  hero_gradient: HeroGradient,
  hero_diagonal: HeroDiagonal,
  hero_cards: HeroCards,
  process_steps: ProcessSteps,
  portfolio_grid: PortfolioGrid,
  case_study_cards: CaseStudyCards,
  contact_split: ContactSplit,
  logo_marquee: LogoMarquee,
  icon_boxes: IconBoxes,
  metrics_large: MetricsLarge,
  // Batch 2 additions
  parallax_section: ParallaxSection,
  announcement_bar: AnnouncementBar,
  accordion_services: AccordionServices,
  footer_multi_column: FooterMultiColumn,
  cta_split: CtaSplit,
  comparison_table: ComparisonTable,
  image_cards: ImageCards,
  divider_wave: DividerWave,
  number_counter: NumberCounter,
  testimonial_featured: TestimonialFeatured,
  // Pricing calculator (T14) — cost_faq reuses FaqAccordion renderer
  pricing_calculator: PricingCalculator,
  cost_faq: FaqAccordion,
};

interface SectionRendererProps {
  section: SectionInstance;
}

export const SectionRenderer: React.FC<SectionRendererProps> = ({ section }) => {
  const Renderer = RENDERERS[section.component_id];
  if (!Renderer) {
    return (
      <div style={{
        padding: '40px 24px', textAlign: 'center',
        background: '#f1f5f9', color: '#94a3b8', borderRadius: 8,
      }}>
        Component: {section.component_id} (no renderer)
      </div>
    );
  }
  return <Renderer section={section} />;
};

export { RENDERERS };
export default SectionRenderer;
